import styled from '@emotion/styled';
import {
  fallbackText,
  getDataFromLink,
  Prismic,
  PrismicLink,
  PrismicText,
} from '@landsbankinn/prismic';
import {
  Footer,
  FooterGroup,
  GroupLink,
  Icon as UiIcon,
  SocialLink,
} from '@landsbankinn/ui';
import React from 'react';

interface IProps {
  text?: Prismic.RichText;
  shortcuts: IShortcuts[];
  socialMedia: ISocialMedia[];
  group: [
    {
      primary: {
        group_title: Prismic.RichText;
      };
      fields: [IItem];
    },
  ];
}
interface IShortcuts {
  s_icon: string;
  s_link: Prismic.Link;
  s_text: Prismic.RichText;
  s_param: string;
}

interface ISocialMedia {
  icon: string;
  link: Prismic.Link;
  link_text: Prismic.RichText;
}

interface IItem {
  link: Prismic.Link;
  link_text: Prismic.RichText;
}

interface IGroup {
  title: Prismic.RichText;
  items: [IItem];
}

const Item = ({ link_text, link }: IItem) => {
  const linkdata = getDataFromLink(link);
  const title = fallbackText(link_text, linkdata.title ?? undefined);

  return (
    <PrismicLink value={link} title={title ?? undefined}>
      <GroupLink title={title ?? ''} />
    </PrismicLink>
  );
};

const Group = ({ title, items }: IGroup) => {
  if (!items) {
    return null;
  }

  return (
    <FooterGroup title={Prismic.text(title)}>
      {items?.map((item, index) => (
        <Item key={`group-item-${index}`} {...item} />
      ))}
    </FooterGroup>
  );
};

const SocialMediaItem = ({ icon, link, link_text }: ISocialMedia) => {
  return (
    <PrismicLink value={link} title={icon}>
      <SocialLink title={Prismic.text(link_text) ?? ''}>
        <UiIcon name={icon} />
      </SocialLink>
    </PrismicLink>
  );
};

export const FooterContainer = ({
  text,
  socialMedia,
  group,
  shortcuts,
}: IProps) => {
  return (
    <Footer
      text={<PrismicText value={text} />}
      groups={group?.map((item, index) => (
        <Group
          key={`group-${index}`}
          title={item?.primary?.group_title}
          items={item?.fields}
        />
      ))}
      shortcuts={shortcuts?.map((item, index) => (
        <IconLink
          key={`icon-${index}`}
          icon={<UiIcon name={item?.s_icon} />}
          text={item?.s_text}
          link={item?.s_link}
          param={item?.s_param}
        />
      ))}
      social={socialMedia?.map((item, index) => (
        <SocialMediaItem key={`social-media-${index}`} {...item} />
      ))}
    />
  );
};

interface IconLinkProps {
  icon: React.ReactNode;
  link: Prismic.Link;
  text: Prismic.RichText;
  param: string;
}

const Host = styled.span`
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;

const A = styled.a`
  &:hover,
  &:focus {
    text-decoration: underline;
  }
`;

const Icon = styled.span`
  display: block;
  margin-right: 10px;
`;

const Text = styled.span`
  display: block;
`;

export const IconLink = ({ icon, link, text, param }: IconLinkProps) => {
  const linkdata = getDataFromLink(link);
  const linkText = fallbackText(text, linkdata.title ?? undefined);

  const query = param === 'Null' ? undefined : param;

  if (link) {
    return (
      <PrismicLink value={link} title={linkText ?? undefined} query={query}>
        <A>
          <Host>
            <Icon>{icon}</Icon>
            <Text>{linkText}</Text>
          </Host>
        </A>
      </PrismicLink>
    );
  }
  return (
    <Host>
      <Icon>{icon}</Icon>
      <Text>{linkText}</Text>
    </Host>
  );
};
